/** @jsx jsx */

import { css, jsx } from '@emotion/core';
import { Fragment } from 'react';

import { Link } from '@zapier/common-components';
import { DateTime } from '@zapier/date';
import { Colors } from '@zapier/theme';

import {
  MeteredTiersTable,
  Price,
} from '@zapier/design-system-revenue/src/components/other';

import { Interval, PlanTier } from '@zapier/design-system-revenue/src/types';

type AmountDueOnNextIntervalProps = {
  isPlanMetered: boolean;
  meteredTiers: PlanTier[];
  nextPaymentDate: string;
  planBillingAmount: number;
  planInterval: Interval;
  planNumFreeMeteredTasks: number;
};

const nextIntervalCss = css`
  background-color: ${Colors.storm};
  color: ${Colors.steel};
  padding: 10px;
`;

const lastParagraphCss = css`
  margin: 0;
`;

const tableStyle = css`
  margin-bottom: 15px;
`;

export const AmountDueOnNextInterval = ({
  isPlanMetered,
  meteredTiers,
  nextPaymentDate,
  planBillingAmount,
  planInterval,
  planNumFreeMeteredTasks,
}: AmountDueOnNextIntervalProps) => {
  const amount = (
    <strong>
      <Price>{planBillingAmount}</Price>
    </strong>
  );

  return (
    <div css={nextIntervalCss}>
      <p>You authorize Zapier to charge you for the amount above.</p>
      <p>Your subscription renews each {planInterval} until you cancel.</p>
      <p>
        The next charge will be {amount}, minus any applicable discounts,
        {nextPaymentDate && (
          <Fragment>
            {' '}
            on{' '}
            <strong>
              <DateTime date={nextPaymentDate} />
            </strong>
            {'. '}
          </Fragment>
        )}
        {isPlanMetered && (
          <Fragment>
            Your plan includes{' '}
            <strong>{planNumFreeMeteredTasks.toLocaleString()} tasks</strong>{' '}
            per month. Extra tasks used will be charged monthly at the below
            rates.
          </Fragment>
        )}
      </p>
      {isPlanMetered && (
        <div css={tableStyle}>
          <MeteredTiersTable tiers={meteredTiers} />
        </div>
      )}
      <p css={lastParagraphCss}>
        By clicking the button below, you understand and agree to these terms
        and Zapier’s{' '}
        <Link href="/privacy" target="_blank">
          privacy policy
        </Link>{' '}
        and{' '}
        <Link href="/terms" target="_blank">
          terms of service
        </Link>
        .
      </p>
    </div>
  );
};
